import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { UserCog, UserPlus } from "lucide-react";

// Componentes
import AdminStatsStrip from "./components/AdminStatsStrip";
import AdminFilterToolbar from "./components/AdminFilterToolbar";
import AdminGrid from "./components/AdminGrid";
//import AdminDetailDrawer from "./components/AdminDetailDrawer";
import AdminActionModal from "./components/AdminActionModal";
import AdminRegistrationForm from "./components/AdminRegistrationForm";
import AssignAdminRoleModal from "./components/AssignAdminRoleModal";

// ----------------------------------------------------------------------
// 1. Interfaces
// ----------------------------------------------------------------------
export interface Admin {
  adminId: number;
  name: string;
  lastName: string;
  userName: string;
  photoLink: string;
  email: string | null;
  phoneNumber: string | null;
  status: string;
  roleLabel?: string;

  blockedRoles?: string[];
  registerDate?: string;
  ciudad?: string | null;
  ultimoAcceso?: string;
}

export interface ApiResponse {
  pagination: {
    totalItemCount: number;
    totalPageCount: number;
    pageSize: number;
    currentPage: number;
    hasPrevious: boolean;
    hasNext: boolean;
  };
  data: any[];
}

export interface AdminStats {
  totalAdmins: number;
}

export const getInitials = (name: string, lastName: string) => {
  return `${name?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
};

const AdminsPage = () => {
  // ESTADOS
  const [activeFilter, setActiveFilter] = useState("todos");
  const [sortOrder, setSortOrder] = useState("recent");
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<ApiResponse["pagination"] | null>(null);

  const [stats, setStats] = useState<AdminStats | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  //const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false);

  //const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // ESTADOS DE SUSPENSIÓN (Se conservan si tu backend usa la configuración dinámica)
  const [suspendType, setSuspendType] = useState<string>("temp");
  const [suspendDuration, setSuspendDuration] = useState<number>(3);
  const [suspendReason, setSuspendReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  //const [suspensionConfig, setSuspensionConfig] = useState<any | null>(null);

  const [toast, setToast] = useState<{ msg: string; type: "success" | "danger" | "info" } | null>(null);

  // FILTROS REDUCIDOS
  const filters = [
    { id: "todos", label: "Todos", queryRole: "" },
    { id: "suspendidos", label: "Suspendidos", queryRole: "suspended" },
  ];

  // LOGICA API
  /* const fetchStats = async () => {
    try {
      // Ajusta este endpoint si tienes uno para obtener el total general de admins
      const response = await apiClient.get<any>("User/GetAdminStats");
      if (response?.data?.adminStats) {
        setStats(response.data.adminStats);
      }
    } catch (error) { console.error("Error cargando estadísticas:", error); }
  }; */

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        pageNumber: currentPage.toString(),
        pageSize: "20"
      });

      if (searchTerm.trim()) {
        params.append("NameOrSurname", searchTerm.trim());
      }

      const endpoint = `User/GetAdministrators?${params.toString()}`;
      const response = await apiClient.get<ApiResponse>(endpoint);

      // Mapeamos los datos respetando la estructura de tu JSON
      let mappedAdmins: Admin[] = (response.data || []).map((item: any) => {
        return {
          adminId: item.userId,
          name: item.name ? item.name.trim() : "",
          lastName: item.lastName ? item.lastName.trim() : "",
          userName: item.userName ? item.userName.trim() : "",
          photoLink: item.photoLink || "",
          email: item.email || null,
          phoneNumber: item.phoneNumber || null,
          status: item.status || "AC",
          roleLabel: "Administrador",
          registerDate: item.registerDate,
        };
      });

      if (activeFilter === "suspendidos") {
        mappedAdmins = mappedAdmins.filter(admin => admin.status !== "AC");
      }

      // ORDENAMIENTO
      if (sortOrder === "oldest") {
        mappedAdmins.sort((a, b) => new Date(a.registerDate || 0).getTime() - new Date(b.registerDate || 0).getTime());
      } else {
        mappedAdmins.sort((a, b) => new Date(b.registerDate || 0).getTime() - new Date(a.registerDate || 0).getTime());
      }

      setAdmins(mappedAdmins);
      setPagination(response.pagination);

      if (!stats && response.pagination) {
        setStats({ totalAdmins: response.pagination.totalItemCount });
      }
    } catch (error) {
      console.error("Error cargando administradores:", error);
      setAdmins([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAdmins(); }, [activeFilter, currentPage, searchTerm, sortOrder]);
  //useEffect(() => { fetchStats(); }, []);

  // HANDLERS
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
    //setIsDrawerOpen(false);
  };

  // ABRIR EL MODAL AL HACER CLIC EN EL BOTÓN
  const openActionModal = (admin: Admin, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedAdmin(admin);
    //setIsDrawerOpen(false);
    setIsModalOpen(true);
  };

  // EJECUTAR EL ENDPOINT AL CONFIRMAR EN EL MODAL
  const confirmAction = async () => {
    if (!selectedAdmin || isUpdating) return;

    setIsUpdating(true);
    const isActive = selectedAdmin.status === "AC";
    
    try {
      const endpoint = isActive ? "DisableAdminRole" : "EnableAdminRole";
      await apiClient.put(`User/${endpoint}?userId=${selectedAdmin.adminId}`);
      
      showToast(`Rol ${isActive ? "deshabilitado" : "habilitado"} correctamente.`, "success");
      setIsModalOpen(false);
      fetchAdmins();
      //fetchStats();
    } catch (error) {
      console.error("Error procesando acción:", error);
      showToast("Ocurrió un error al procesar la acción.", "danger");
    } finally {
      setIsUpdating(false);
    }
  };

  /*
  const openDrawer = async (admin: Admin) => {
    setSelectedAdmin(admin);
    setIsDrawerOpen(true);
    if (!admin.adminId) return;

    setIsDrawerLoading(true);
    try {
      // Este endpoint debes adaptarlo al que obtenga el perfil detallado del admin
      const response = await apiClient.get<any>(`User/GetAdminCardInfo/${admin.adminId}`);
      const details = response.data;

      if (details) {
        setSelectedAdmin((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            photoLink: details.photoLink || prev.photoLink,
            email: details.email || prev.email,
            phoneNumber: details.phoneNumber || prev.phoneNumber,
            ciudad: details.cityName || "N/A",
            registerDate: details.registerDate || prev.registerDate,
          };
        });
      }
    } catch (error) {
      // El toast se omite aquí silenciosamente si no tienes el endpoint de detalle aún
    }
    finally { setIsDrawerLoading(false); } 
    
  };
  */

  const showToast = (msg: string, type: "success" | "danger" | "info" = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  return (
    <div className="flex w-full h-screen bg-gray-50 text-gray-900 font-sans overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* TOPBAR FIJO */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 md:px-[32px] py-3 md:py-4 border-b border-gray-200 bg-white z-20 shrink-0 gap-3 sm:gap-0">
          <div>
            <h1 className="text-[20px] font-bold flex items-center gap-[10px] m-0 text-gray-900">
              <UserCog size={22} color="#4f6df5" strokeWidth={2} />
              Administradores
            </h1>
            <p className="text-xs text-gray-500 mt-1 m-0">
              Gestión del equipo de administración
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              className="text-xs font-semibold px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 flex items-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
              onClick={() => setIsAssignRoleModalOpen(true)}
            >
              <UserPlus size={14} strokeWidth={2.5} />
              Asignar rol a existente
            </button>

            <button
              className="text-xs font-semibold px-4 py-2 rounded-lg bg-[#4f6df5] text-white flex items-center gap-1.5 hover:bg-[#3b5bdb] transition-colors shadow-sm"
              onClick={() => setIsRegisterModalOpen(true)}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Nuevo administrador
            </button>
          </div>
        </div>

        {/* Stats, Filtros y el Grid */}
        <div className="flex-1 overflow-y-auto bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">

          <AdminStatsStrip stats={stats} />

          <div className="sticky top-0 z-10 shadow-sm bg-white">
            <AdminFilterToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filters={filters}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
              sortOrder={sortOrder}
              onSortChange={(val) => { setSortOrder(val); setCurrentPage(1); }}
            />
          </div>

          <div className="p-4 sm:p-5 md:p-7">
            <AdminGrid
              admins={admins}
              loading={loading}
              //onAdminClick={openDrawer}
              onAdminClick={() => {}}
              onActionClick={openActionModal}
            />

            {/* Paginación */}
            {pagination && pagination.totalPageCount > 1 && (
              <div className="flex justify-center items-center gap-3 mt-8 pt-5 border-t border-gray-200">
                <button
                  disabled={!pagination.hasPrevious}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  Anterior
                </button>
                <span className="text-xs text-gray-500 font-medium">Página {pagination.currentPage} de {pagination.totalPageCount}</span>
                <button
                  disabled={!pagination.hasNext}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODALES Y DRAWERS */}
      {/*
      <AdminDetailDrawer
        selectedAdmin={selectedAdmin}
        isDrawerOpen={isDrawerOpen}
        isDrawerLoading={isDrawerLoading}
        onClose={() => setIsDrawerOpen(false)}
        onActionClick={openActionModal}
      />
      */}

      <AdminActionModal
        isOpen={isModalOpen}
        selectedAdmin={selectedAdmin}
        isUpdating={isUpdating}
        suspensionConfig={null}
        suspendType={suspendType}
        setSuspendType={(val: string) => setSuspendType(val)}
        suspendDuration={suspendDuration}
        setSuspendDuration={(val: number) => setSuspendDuration(val)}
        suspendReason={suspendReason}
        setSuspendReason={setSuspendReason}
        reasonError={reasonError}
        setReasonError={setReasonError}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmAction}
      />

      <AdminRegistrationForm
        open={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={() => {
          showToast("Administrador registrado correctamente", "success");
          fetchAdmins();
          /* fetchStats(); */
        }}
      />

      {/* Modal de asignar rol */}
      <AssignAdminRoleModal
        isOpen={isAssignRoleModalOpen}
        onClose={() => setIsAssignRoleModalOpen(false)}
        onSuccess={() => {
          showToast("Rol asignado correctamente a usuario", "success");
          fetchAdmins();
          /* fetchStats(); */
        }}
      />

      {/* TOAST */}
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {toast && (
        <div className="fixed bottom-6 right-6 z-[200] bg-white rounded-[10px] text-[13px] font-semibold py-3 px-5 shadow-lg border"
          style={{
            animation: "toastIn 0.3s ease forwards",
            borderColor: toast.type === "success" ? "#34d399" : toast.type === "danger" ? "#f87171" : "#e5e7eb",
            color: toast.type === "success" ? "#059669" : toast.type === "danger" ? "#dc2626" : "#1f2937"
          }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminsPage;